import { AxiosError } from 'axios'
import React, { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { RouteComponentProps } from 'react-router'
import { $enum } from 'ts-enum-util'

import { ProductSearch } from './ProductSearch'
import {
  getColumnForSortField,
  getDerivedFilter,
  getSortFieldForColumn,
  getUpdatedFiltersForTag
} from './ProductSearch.models'
import { AnnounceMode } from '../../../components/ClinAnnounceBar/ClinAnnounceBar'
import {
  decodeObjToUrlParams,
  getParamValueFor,
  IPagination,
  useUpdateQueryParam
} from '../../../components/ClinPagination/ClinPagination.model'
import { SortDirectionType } from '../../../components/ClinTableOrderToggle/ClinTableOrderToggle'
import {
  UserRoleRecord,
  UserRole,
  isAusGaUser,
  ProductItemAvailabilityStatus
} from '../../../constants'
import { CountryAlphaCodes } from '../../../constants/countryAlpha2Codes'
import { useAppContext } from '../../../context/app'
import { createAnnounceEvent } from '../../../events/AnnounceEvent'
import { useEffectOnlyOnce } from '../../../hooks/useEffectOnlyOnce/useEffectOnlyOnce'
import { AnalyticsEvent } from '../../../services/Analytics/AnalyticsEvent'
import { AnalyticsPageEvent } from '../../../services/Analytics/AnalyticsPageEvent'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import {
  AuthError,
  cancelGetProducts,
  cancelGetTherapeuticAreas,
  getProducts,
  getSearchFacetsForCountry
} from '../../../services/ApiService'
import { IProductCatalog } from '../../../types'
import {
  CatalogDto,
  FacetDto,
  FieldFacetsDto,
  ProductSearchDto
} from '../../../types/swaggerTypes'
import { useOnMount } from '../../../utils/useOnMount'
import { extractProductNameForSearch } from '../../../utils/dosageFormUtils'
import {
  ProductColumn,
  isAccessProgram,
  isExceptionTag,
  getAvailabilityDisplayText,
  ProductFilterOptions
} from '../Product.models'
import useChangeBackgroundColor from '../../../hooks/useChangeBackgroundColor/useChangeBackgroundColor'
import { ClinTheme } from '../../../ClinTheme'
import { getMatchingDefaultWarehouseCodes } from '../ProductVariant/ProductVariant.models'
import { getVariantInformationForCatalogItem } from '../../../services/PortfolioJourneys/PortfolioJourney.model'
import { IAugmentedCatalogItemDto } from '../../../services/PortfolioJourneys/PortfolioJourney.types'

const rowsPerPage: number = 10

const defaultPagination: IPagination = {
  count: 0,
  skip: 0,
  take: rowsPerPage,
  total: 0
}

const defaultSearchParams: ProductSearchDto = {
  query: '',
  filter: {
    tags: [],
    availabilities: [],
    countryCode: 'UNKNOWN',
    derivedFromFilter: ''
  },
  pagination: {
    skip: 0,
    take: rowsPerPage
  },
  sorting: {
    sortBy: `countryAvailability/UNKNOWN`,
    order: SortDirectionType.Ascending
  }
}

const defaultAusGaFilter: string[] = ['Available', 'Available On Request']
export const defaultAusGaDerivedFromFilter: string = 'Generic'

interface IProductSearchProps extends RouteComponentProps {}

const decodeNestedURIComponent = (str: string): string => {
  try {
    const decoded = decodeURIComponent(str)
    return decoded === str ? decoded : decodeNestedURIComponent(decoded)
  } catch {
    return str
  }
}

export const ProductSearchContainer: React.FC<IProductSearchProps> = ({
  location,
  history
}) => {
  const { t } = useTranslation()
  const { dispatch, portfolioCountryCode, userRole, institute } =
    useAppContext()
  const isMaUser = !!(userRole && UserRoleRecord[userRole as UserRole].isMaUser)
  const [isLoading, setIsLoading] = React.useState<boolean>(true)
  const [products, setProducts] = React.useState<IProductCatalog[] | undefined>(
    undefined
  )
  const [filteredProducts, setFilteredProducts] = useState<IProductCatalog[]>(
    []
  )
  const [hasInitialLoad, setHasInitialLoad] = useState<boolean>(false)
  const [areaFilters, setAreaFilters] = React.useState<FacetDto[]>([])
  const [availabilityFilters, setAvailabilityFilters] = React.useState<
    FacetDto[]
  >([])
  const [isAliasSearchResult, setIsAliasSearchResult] =
    React.useState<boolean>(false)

  useChangeBackgroundColor(ClinTheme.colors.lightGrey)
  // Restore page state from URL params
  const urlSearchParams = React.useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  )

  // Query - Display query (what user sees in the input)
  const initialQuery = React.useMemo(() => {
    const raw = urlSearchParams.get('q') ?? ''
    const withoutStar = raw === '*' ? '' : raw
    return decodeNestedURIComponent(withoutStar)
  }, [urlSearchParams])

  // Track the display query (what user sees in input and URL) separately from server query
  const [displayQuery, setDisplayQuery] = React.useState<string>(initialQuery)

  // Server query - Parsed query (what gets sent to the API)
  const initialServerQuery = React.useMemo(() => {
    if (!initialQuery || initialQuery.trim() === '') {
      return ''
    }
    const { serverQuery } = extractProductNameForSearch(initialQuery)
    return serverQuery
  }, [initialQuery])

  const derivedFilter = React.useMemo(() => {
    // Empty string means receiving programs and products simultaneously
    return getDerivedFilter(portfolioCountryCode, userRole) //changed related to clos-1614 aus ma users can see products
  }, [portfolioCountryCode, userRole])

  // Restore applied/selected filters
  const [selectedAreaFilters, setSelectedAreaFilters] = React.useState<
    FacetDto[]
  >(() => {
    let previousSelectedFilters: FacetDto[] = []
    const appliedFilters = urlSearchParams.get('selectedAreaFilters')
    if (appliedFilters) {
      previousSelectedFilters = decodeObjToUrlParams(appliedFilters)
      defaultSearchParams.filter.tags = [
        ...previousSelectedFilters.map((f) => f.value)
      ]
    }
    return previousSelectedFilters
  })

  const [selectedAvailabilityFilters, setSelectedAvailabilityFilters] =
    React.useState<FacetDto[]>(() => {
      let previousSelectedFilters: FacetDto[] = []
      const appliedFilters = urlSearchParams.get('selectedAvailabilityFilters')
      if (appliedFilters) {
        previousSelectedFilters = decodeObjToUrlParams(appliedFilters)
        defaultSearchParams.filter.availabilities = [
          ...previousSelectedFilters.map((f) => f.value)
        ]
      }

      return previousSelectedFilters
    })

  const [selectedFilterOptions, setSelectedFilterOptions] = React.useState<
    string[]
  >([])

  // Restore page Size
  const [productsPerPage, setProductsPerPage] = React.useState<number>(() => {
    const previousPageSize = urlSearchParams.get('pageSize')
    return previousPageSize ? parseInt(previousPageSize) : rowsPerPage
  })

  // Restore pagination from URL params
  defaultSearchParams.pagination.take = getParamValueFor(
    'pageSize',
    urlSearchParams,
    rowsPerPage
  )
  // PageIndex
  defaultPagination.take = defaultSearchParams.pagination.take
  defaultSearchParams.pagination.skip =
    getParamValueFor('pageIndex', urlSearchParams) *
    defaultSearchParams.pagination.take

  const [pagination, setPagination] =
    React.useState<IPagination>(defaultPagination)

  // Restore sort by column from URL params and update query
  const [sortColumn, setSortColumn] = React.useState<ProductColumn>(() => {
    const sortByColumn = urlSearchParams.get('sortBy')
    const previousSortBy = sortByColumn && decodeURIComponent(sortByColumn)
    const defaultSortByColumn = getColumnForSortField(
      defaultSearchParams.sorting.sortBy,
      portfolioCountryCode
    )
    const restoredSortByColumn = $enum(ProductColumn).asValueOrDefault(
      previousSortBy,
      defaultSortByColumn
    )
    defaultSearchParams.sorting.sortBy = getSortFieldForColumn(
      restoredSortByColumn,
      portfolioCountryCode
    )
    return restoredSortByColumn
  })

  // Restore sort direction from URL params
  const [sortDirection, setSortDirection] = React.useState<SortDirectionType>(
    () => {
      const previousSortDirection = urlSearchParams.get('sortDirection')
      const restoredSortDirection = $enum(SortDirectionType).asValueOrDefault(
        previousSortDirection,
        portfolioCountryCode === CountryAlphaCodes.Australia //for aus users we want desc order
          ? SortDirectionType.Descending
          : SortDirectionType.Ascending
      )
      defaultSearchParams.sorting.order = restoredSortDirection.toString()
      return restoredSortDirection
    }
  )

  //if user is aus ga only we should hide access program clos-1610
  if (isAusGaUser(portfolioCountryCode, userRole)) {
    defaultSearchParams.filter.availabilities = defaultAusGaFilter
  }

  // Construct the search query
  defaultSearchParams.filter.countryCode = portfolioCountryCode
  const [searchParams, setSearchParams] = React.useState<ProductSearchDto>({
    ...defaultSearchParams,
    query: initialServerQuery, // Use parsed server query for API calls
    filter: {
      ...defaultSearchParams.filter,
      derivedFromFilter: derivedFilter
    },
    sorting: initialServerQuery
      ? { sortBy: '', order: SortDirectionType.None }
      : defaultSearchParams.sorting
  })

  const handlePageClicked = (selectedPageIndex: number) => {
    cancelGetProducts()
    setIsLoading(true)
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: (selectedPageIndex - 1) * productsPerPage,
        take: productsPerPage
      }
    })
  }

  const handlePageSizeChange = (pageSize: number) => {
    cancelGetProducts()
    setIsLoading(true)
    setProductsPerPage(pageSize)
    setSearchParams({
      ...searchParams,
      pagination: {
        skip: 0,
        take: pageSize
      }
    })
  }

  const handleRowClicked = (itemId: string | number, isProgram: boolean) => {
    cancelGetProducts()
    history.push(
      isProgram ? `/programs/access-programs/${itemId}` : `/product/${itemId}`
    )
  }

  const handleFilterToggle = (tag: FacetDto, filterGroupTitle: string) => {
    cancelGetProducts()
    setIsLoading(true)
    const isAvailabilityFilter =
      filterGroupTitle === t('product_catalogue:filter.title')
    const updatedFilters = getUpdatedFiltersForTag(
      tag,
      isAvailabilityFilter ? selectedAvailabilityFilters : selectedAreaFilters
    )

    let updateSearchParams: ProductSearchDto

    if (isAvailabilityFilter) {
      updateSearchParams = {
        ...searchParams,
        pagination: {
          ...searchParams.pagination,
          skip: 0
        },
        filter: {
          ...searchParams.filter,
          availabilities: [...updatedFilters.map((f) => f.value)]
        }
      }
      setSelectedAvailabilityFilters(updatedFilters)
    } else {
      updateSearchParams = {
        ...searchParams,
        pagination: {
          ...searchParams.pagination,
          skip: 0
        },
        filter: {
          ...searchParams.filter,
          tags: [...updatedFilters.map((f) => f.value)]
        }
      }
      setSelectedAreaFilters(updatedFilters)
    }

    setSearchParams(updateSearchParams)
  }

  const handleClearAreaFilters = () => {
    cancelGetProducts()
    setIsLoading(true)
    setSearchParams({
      ...searchParams,
      pagination: {
        ...searchParams.pagination,
        skip: 0
      },
      filter: {
        ...searchParams.filter,
        tags: []
      }
    })
    setSelectedAreaFilters([])
  }

  const toggleSortDirection = (
    current: SortDirectionType
  ): SortDirectionType => {
    if (current === SortDirectionType.None) {
      return SortDirectionType.Descending
    }
    if (current === SortDirectionType.Descending) {
      return SortDirectionType.Ascending
    }
    return SortDirectionType.None
  }

  const handleToggleSort = (columnName: ProductColumn) => {
    cancelGetProducts()
    setIsLoading(true)
    const newSortDirection =
      sortColumn === columnName
        ? toggleSortDirection(sortDirection)
        : SortDirectionType.Descending
    setSortDirection(newSortDirection)
    setSortColumn(columnName)
    const sortField = getSortFieldForColumn(columnName, portfolioCountryCode)
    setSearchParams({
      ...searchParams,
      sorting: {
        sortBy:
          newSortDirection === SortDirectionType.None
            ? `countryAvailability/${portfolioCountryCode}`
            : sortField,
        order: newSortDirection
      }
    })
  }

  const handleOnChange = (query: string) => {
    cancelGetProducts()
    setIsLoading(true)
    // Reset alias search flag when starting a new search
    setIsAliasSearchResult(false)

    // Update the display query (what user sees in input and URL)
    setDisplayQuery(query)

    // Parse the query to extract only the product name for the server request
    // This enables exact match behavior like in AutoSuggest
    const { serverQuery } = extractProductNameForSearch(query)
    // const { serverQuery, dosageForm } = extractProductNameForSearch(query) // TODO: Use dosageForm for client-side filtering

    setSearchParams((prevState) => ({
      ...prevState,
      pagination: {
        ...prevState.pagination,
        skip: 0
      },
      query: serverQuery, // Send only the product name to the server
      sorting: serverQuery
        ? { sortBy: '', order: SortDirectionType.None }
        : prevState.sorting
    }))

    // TODO: Store dosageForm for potential client-side filtering if needed
    // Currently, server returns all variants for the product (like AutoSuggest exact match)
  }

  const handleToggleProductRow = (catalogItemName: string) => {
    products &&
      setProducts([
        ...products.map((product: IProductCatalog) => {
          if (product.catalogDocumentId === catalogItemName) {
            product.isOpen = !product.isOpen
          }
          return product
        })
      ])
  }

  const handleSetSelectedFilterOption = (option: string) => {
    cancelGetProducts()
    setIsLoading(true)

    const isSelected = selectedFilterOptions.includes(option)
    let newSelectedOptions: string[]

    if (isSelected) {
      newSelectedOptions = selectedFilterOptions.filter((opt) => opt !== option)
    } else {
      newSelectedOptions = [...selectedFilterOptions, option]
    }

    setSelectedFilterOptions(newSelectedOptions)

    const updatedSearchParams = {
      ...searchParams,
      pagination: {
        ...searchParams.pagination,
        skip: 0
      }
    }

    updatedSearchParams.filter.availabilities = []
    updatedSearchParams.filter.derivedFromFilter = ''

    const hasUnlicensedProducts = newSelectedOptions.includes(
      ProductFilterOptions.Products
    )
    const hasPrograms = newSelectedOptions.includes(
      ProductFilterOptions.Programs
    )

    const isAusGa = isAusGaUser(portfolioCountryCode, userRole)

    if (hasUnlicensedProducts && hasPrograms) {
      updatedSearchParams.filter.availabilities = []
      updatedSearchParams.filter.derivedFromFilter = ''
    } else if (hasUnlicensedProducts) {
      updatedSearchParams.filter.availabilities = isAusGa
        ? defaultAusGaFilter
        : ['Available', 'Available On Request']
      updatedSearchParams.filter.derivedFromFilter = isAusGa
        ? defaultAusGaDerivedFromFilter
        : 'Generic'
    } else if (hasPrograms) {
      updatedSearchParams.filter.derivedFromFilter = 'ManagedAccessProgram'
    } else if (isAusGa) {
      updatedSearchParams.filter.availabilities = defaultAusGaFilter
      updatedSearchParams.filter.derivedFromFilter =
        defaultAusGaDerivedFromFilter
    }

    setSearchParams(updatedSearchParams)
  }

  useOnMount(() => {
    getSearchFacetsForCountry(portfolioCountryCode)
      .then((response) => {
        // Availability
        const availabilityFacets: FieldFacetsDto | undefined =
          response.data.facets.find(
            (f) => f.field === `countryAvailability/${portfolioCountryCode}`
          )
        let availabilityValues = availabilityFacets?.values
        if (availabilityValues && isAusGaUser(portfolioCountryCode, userRole)) {
          //condition added related to clos-1610 (Aus ga only user should not be able to filter access program products)
          availabilityValues = availabilityValues.filter(
            (a) => a.value !== 'Access Program'
          )
        }

        availabilityValues && setAvailabilityFilters(availabilityValues)
      })
      .catch((error: AxiosError) => {
        const { code, message } = error
        // If request is cancelled continue
        if (error.message === AuthError.RequestCancelled) {
          return
        }
        // If we have a full error show it
        if (error.response) {
          const { title, detail } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching therapeutic areas. ${title ?? ''} ${
                message ?? ''
              }`
            )
          )
          console.warn(title, detail)
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching therapeutic areas. ${message} ${
                code ?? ''
              }`
            )
          )
        }

        analyticsServiceSingleton.trackError(
          error,
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
        setIsLoading(false)
      })

    return () => {
      cancelGetTherapeuticAreas()
    }
  })

  // Sync searchParams with URL changes
  useEffect(() => {
    const currentPageSize = getParamValueFor(
      'pageSize',
      urlSearchParams,
      rowsPerPage
    )
    const currentPageIndex = getParamValueFor('pageIndex', urlSearchParams, 0)
    const currentSkip = currentPageIndex * currentPageSize

    // Update display query when URL changes
    setDisplayQuery(initialQuery)

    setSearchParams((prevParams) => ({
      ...prevParams,
      query: initialServerQuery, // Use parsed server query for API calls
      pagination: {
        skip: currentSkip,
        take: currentPageSize
      },
      sorting: initialServerQuery
        ? { sortBy: '', order: SortDirectionType.None }
        : prevParams.sorting
    }))

    // Also update the productsPerPage state if it changed
    setProductsPerPage(currentPageSize)

    // Update pagination state
    setPagination((prevPagination) => ({
      ...prevPagination,
      skip: currentSkip,
      take: currentPageSize
    }))
  }, [initialQuery, initialServerQuery, urlSearchParams])

  // Get products
  useEffect(() => {
    // Below is a potential fix for how the view all might work - requires a date time stamp in the AutoSuggest
    // Handles the situation where you are on the current page and click View all products
    const queryParam = new URLSearchParams(location.search)
    const productQuery = queryParam.get('q')
    if (productQuery === '*') {
      history.push({
        pathname: location.pathname,
        search: ``
      })
      // Reset it all!
      setIsLoading(true)
      setSelectedAreaFilters([])
      setPagination(defaultPagination)
      setProducts([])
      setSearchParams((prevState) => ({
        ...defaultSearchParams,
        filter: { ...prevState.filter }
      }))
      return
    }

    // If there's a search query, adjust the search parameters to use relevancy sorting
    // Use the server query (parsed) to determine if we should use relevance sorting
    if (
      searchParams.query &&
      searchParams.query !== '' &&
      searchParams.query !== '*'
    ) {
      if (searchParams.sorting.sortBy !== '') {
        setSearchParams((prevParams) => ({
          ...prevParams,
          sorting: { sortBy: '', order: SortDirectionType.None }
        }))

        // Reset sort column and direction state to match the relevance sorting
        const defaultSortColumn = getColumnForSortField(
          '',
          portfolioCountryCode
        )
        setSortColumn(defaultSortColumn)
        setSortDirection(SortDirectionType.None)

        return // useEffect will run again with updated searchParams
      }
    }

    getProducts(searchParams)
      .then((response) => {
        // If no results found and we have a query, try again with includeAliases: true
        if (
          response.data.result.length === 0 &&
          productQuery &&
          productQuery.trim() !== '' &&
          productQuery !== '*' &&
          !searchParams.filter.includeAliases
        ) {
          return getProducts({
            ...searchParams,
            filter: {
              ...searchParams.filter,
              includeAliases: true
            }
          }).then((aliasResponse) => {
            return {
              ...aliasResponse,
              wasAliasSearch: true
            }
          })
        }
        return { ...response, wasAliasSearch: false }
      })
      .then((response) => {
        if (productQuery && productQuery !== '') {
          analyticsServiceSingleton.trackEvent(
            AnalyticsEvent.SubmitSearchQuery,
            {
              query: productQuery,
              searchLocation: 'product catalogue',
              searchAPI: 'search'
            }
          )
          analyticsServiceSingleton.trackEvent(
            AnalyticsEvent.ResultsForSearchQuery,
            {
              query: productQuery,
              numberResults: response.data.result.length
            }
          )
        }

        // Set alias search flag based on whether this was a fallback search
        setIsAliasSearchResult(response.wasAliasSearch || false)

        if (response) {
          const productsFromApi: CatalogDto[] | null = response.data.result
          const therapeuticAreaFacets = response.data.facets?.find(
            (facet) => facet.field === 'tags'
          )
          setAreaFilters(
            therapeuticAreaFacets?.values?.filter(
              (facet) => !isExceptionTag(facet.value)
            ) ?? []
          )
          productsFromApi.forEach((product: IProductCatalog) => {
            const { tags, countryAvailability } = product
            product.isBookmarkable = isAccessProgram(tags)
            product.availabilityText =
              countryAvailability &&
              getAvailabilityDisplayText(
                countryAvailability,
                portfolioCountryCode,
                t
              )
            product.tags = product.tags.filter((t) => !isExceptionTag(t))
          })
          setProducts(productsFromApi)
          const paginationDto = response.data.pagination
          setPagination({
            count: paginationDto.count,
            skip: paginationDto.skip,
            take: paginationDto.take,
            total: paginationDto.total
          })
        }
        setHasInitialLoad(true)
        setIsLoading(false)
      })
      .catch((error: AxiosError) => {
        const { code, message } = error

        // If request is cancelled continue
        if (error.message === AuthError.RequestCancelled) {
          return
        }
        // If we have a full error show it
        if (error.response) {
          const { title, detail } = error.response.data
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching products. ${title ?? ''} ${
                message ?? ''
              }`
            )
          )
          console.warn(title, detail)
        } else {
          dispatch(
            createAnnounceEvent(
              AnnounceMode.Error,
              `There was an error fetching products. ${message} ${code ?? ''}`
            )
          )
        }

        analyticsServiceSingleton.trackError(
          error,
          JSON.parse(localStorage.getItem('current_user') || '{}')
        )
        setIsLoading(false)
      })

    return () => {
      cancelGetProducts()
    }
  }, [
    dispatch,
    history,
    location.pathname,
    location.search,
    portfolioCountryCode,
    searchParams
  ])

  // Track changes to pagination and tabs as they update and update query string if it changes
  // Use the display query for URL so users see what they typed
  useUpdateQueryParam({ q: displayQuery })
  useUpdateQueryParam({
    selectedAreaFilters
  })
  useUpdateQueryParam({
    selectedAvailabilityFilters
  })

  useUpdateQueryParam({ sortBy: sortColumn.toString() })
  useUpdateQueryParam({ sortDirection: sortDirection.toString() })
  useUpdateQueryParam({ pageSize: searchParams.pagination.take })
  useUpdateQueryParam({
    pageIndex: searchParams.pagination.skip / searchParams.pagination.take
  })

  type Deps = [boolean]
  useEffectOnlyOnce(
    () => {
      analyticsServiceSingleton.trackPageView(
        AnalyticsPageEvent.ViewProductCatalogue,
        {}
      )
    },
    [isLoading],
    (dependencies: Deps) => dependencies[0] === false
  )

  // Process products through portfolio journey logic to filter hidden items
  useEffect(() => {
    if (products && products.length > 0) {
      const processedProducts = products.map((product: IProductCatalog) => {
        const processedItems = product?.items
          .map((catalogItem) => {
            const availableWarehouseCodes: string[] =
              getMatchingDefaultWarehouseCodes(
                catalogItem.stockLevels,
                portfolioCountryCode,
                institute.data?.warehouses
              )

            const augmentedCatalogItem = {
              ...catalogItem,
              warehouseCodes: availableWarehouseCodes,
              warehouses: undefined
            }

            const augmentedItem: IAugmentedCatalogItemDto =
              getVariantInformationForCatalogItem(
                augmentedCatalogItem,
                portfolioCountryCode,
                undefined,
                undefined
              )

            return {
              ...augmentedCatalogItem,
              ...augmentedItem
            }
          })
          .filter(
            (item) =>
              item?.availableStatus !== ProductItemAvailabilityStatus.HIDDEN
          )

        return {
          ...product,
          items: processedItems
        }
      })

      setFilteredProducts(processedProducts)
    } else {
      setFilteredProducts([])
    }
  }, [products, portfolioCountryCode, institute.data])

  const filterHiddenVariants = useCallback(() => {
    return filteredProducts.filter((product) => product?.items.length > 0)
  }, [filteredProducts])

  return (
    <ProductSearch
      isLoading={isLoading || !hasInitialLoad}
      isMaUser={isMaUser}
      initialQuery={displayQuery}
      products={filterHiddenVariants()}
      pagination={pagination}
      areaFilters={areaFilters}
      selectedAreaFilters={selectedAreaFilters}
      availabilityFilters={availabilityFilters}
      selectedAvailabilityFilters={selectedAvailabilityFilters}
      selectedFilterOptions={selectedFilterOptions}
      selectedSortColumn={sortColumn}
      selectedSortDirection={sortDirection}
      userCountry={portfolioCountryCode}
      searchParams={searchParams}
      isAliasSearchResult={isAliasSearchResult}
      handleRowClicked={(itemId, isProgram) =>
        handleRowClicked(itemId, isProgram)
      }
      handlePageClicked={handlePageClicked}
      handlePageSizeChange={handlePageSizeChange}
      handleFilterToggle={handleFilterToggle}
      handleClearAreaFilters={handleClearAreaFilters}
      handleToggleSort={handleToggleSort}
      handleOnChange={handleOnChange}
      handleSetSelectedFilterOption={handleSetSelectedFilterOption}
      handleToggleProductRow={handleToggleProductRow}
    />
  )
}
